import type {
  FileItem,
  ProcessAction,
  ProcessMyTask,
  ProcessStepView,
  ProcessSummary,
  ProcessTaskView,
  ProcessView,
} from '@erp/shared';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import clsx from 'clsx';
import { Check, Clock, Minus } from 'lucide-react';
import { type FormEvent, type ReactNode, useState } from 'react';
import { ApiError, api } from '@/lib/api';
import { formatDay, formatMoment } from '@/lib/format';
import { Dialog } from './dialog';
import { FileList, FileUpload } from './file-upload';
import { Badge, Button, Field, Input, Textarea } from './ui';
import { UserPicker } from './user-picker';

const DECISION_LABELS: Record<string, string> = {
  approve: 'Согласовано',
  reject: 'Отклонено',
  done: 'Выполнено',
  return: 'Возвращено на доработку',
};

/** Всё, что зависит от хода маршрута, перечитываем разом. */
function useRefreshProcess() {
  const qc = useQueryClient();
  return async () => {
    await Promise.all([
      qc.invalidateQueries({ queryKey: ['bpm'] }),
      qc.invalidateQueries({ queryKey: ['vacations'] }),
      qc.invalidateQueries({ queryKey: ['requests'] }),
      qc.invalidateQueries({ queryKey: ['absences'] }),
      qc.invalidateQueries({ queryKey: ['notifications'] }),
    ]);
  };
}

/** Кратко для списков: «Согласование: Демидов Пётр» или «Пауза до 15 ноября». */
export function ProcessHint({ summary }: { summary: ProcessSummary | null }) {
  if (summary?.status !== 'running') return null;
  if (summary.waitUntil) {
    return (
      <span className="text-[12.5px] text-steel">Пауза до {formatDay(summary.waitUntil.slice(0, 10))}</span>
    );
  }
  return (
    <span className="text-[12.5px] text-steel">
      {summary.stepTitle}
      {summary.assignees.length > 0 && `: ${summary.assignees.map((a) => a.fullName).join(', ')}`}
    </span>
  );
}

const STATUS_TONES: Record<string, 'neutral' | 'ok' | 'warn' | 'danger'> = {
  pending: 'warn',
  approved: 'ok',
  paid: 'ok',
  issued: 'ok',
  registered: 'ok',
  closed: 'neutral',
  rejected: 'danger',
  cancelled: 'neutral',
};

export function EntityStatus({ status, label }: { status: string; label: string }) {
  return <Badge tone={STATUS_TONES[status] ?? 'neutral'}>{label}</Badge>;
}

/** Карточка маршрута: мои задания сверху, ниже — ход согласования по шагам. */
export function RoutePanel({ route }: { route: ProcessView }) {
  return (
    <div className="flex flex-col gap-6">
      {route.myTasks.map((t) => (
        <MyTaskCard key={t.id} task={t} />
      ))}
      <section className="panel">
        <header className="flex flex-wrap items-baseline justify-between gap-2 border-b border-line px-5 py-3.5">
          <h2 className="font-semibold">Ход согласования</h2>
          <span className="text-[13px] text-steel">
            {route.status === 'running' && 'идёт'}
            {route.status === 'completed' &&
              `завершено ${route.finishedAt ? formatMoment(route.finishedAt) : ''}`}
            {route.status === 'rejected' && 'отклонено'}
            {route.status === 'cancelled' && 'отменено'}
          </span>
        </header>
        <ol className="px-5 py-4">
          {route.steps.map((s, i) => (
            <StepItem
              key={s.id}
              step={s}
              last={i === route.steps.length - 1}
              tasks={route.tasks.filter((t) => t.stepId === s.id)}
              waitUntil={s.state === 'current' && s.type === 'wait' ? route.waitUntil : null}
              canReassign={route.canReassign}
            />
          ))}
        </ol>
      </section>
    </div>
  );
}

function StepMarker({ state }: { state: ProcessStepView['state'] }) {
  const base = 'relative z-10 flex size-6 shrink-0 items-center justify-center rounded-full';
  if (state === 'done')
    return (
      <span className={clsx(base, 'bg-ok text-white')}>
        <Check className="size-3.5" aria-hidden />
      </span>
    );
  if (state === 'current')
    return (
      <span className={clsx(base, 'border-2 border-signal bg-surface')}>
        <span className="size-2 rounded-full bg-signal" />
      </span>
    );
  if (state === 'skipped')
    return (
      <span className={clsx(base, 'bg-sunken text-steel')}>
        <Minus className="size-3.5" aria-hidden />
      </span>
    );
  return <span className={clsx(base, 'border-2 border-line-strong bg-surface')} />;
}

const STATE_LABEL: Record<ProcessStepView['state'], string> = {
  done: 'выполнен',
  current: 'текущий шаг',
  pending: 'впереди',
  skipped: 'пропущен',
};

function StepItem({
  step,
  tasks,
  last,
  waitUntil,
  canReassign,
}: {
  step: ProcessStepView;
  tasks: ProcessTaskView[];
  last: boolean;
  waitUntil: string | null;
  canReassign: boolean;
}) {
  const muted = step.state === 'pending' || step.state === 'skipped';
  return (
    <li className="relative flex gap-3 pb-4 last:pb-0">
      {!last && <span aria-hidden className="absolute left-[11px] top-6 bottom-0 w-px bg-line" />}
      <StepMarker state={step.state} />
      <div className="min-w-0 flex-1 pt-0.5">
        <p className={clsx('font-medium', muted && 'text-steel', step.type === 'notify' && 'font-normal')}>
          {step.title}
          <span className="sr-only"> — {STATE_LABEL[step.state]}</span>
        </p>
        {waitUntil && (
          <p className="mt-0.5 flex items-center gap-1.5 text-[13px] text-steel">
            <Clock className="size-3.5" aria-hidden />
            Продолжится {formatMoment(waitUntil)}
          </p>
        )}
        {tasks.length > 0 && (
          <ul className="mt-1.5 flex flex-col gap-2">
            {tasks.map((t) => (
              <TaskLine key={t.id} task={t} canReassign={canReassign} />
            ))}
          </ul>
        )}
      </div>
    </li>
  );
}

function TaskLine({ task, canReassign }: { task: ProcessTaskView; canReassign: boolean }) {
  const [reassigning, setReassigning] = useState(false);
  let head: ReactNode;
  if (task.status === 'open') {
    head = (
      <>
        <span className="text-steel">Ждёт решения:</span> {task.assignee.fullName}
        {canReassign && (
          <button type="button" className="link ml-2 text-[12.5px]" onClick={() => setReassigning(true)}>
            Переназначить
          </button>
        )}
      </>
    );
  } else if (task.status === 'skipped') {
    head = (
      <span className="text-steel">
        {task.assignee.fullName} — {task.note?.toLowerCase()}
      </span>
    );
  } else if (task.status === 'cancelled') {
    head = <span className="text-steel">{task.assignee.fullName} — задание снято</span>;
  } else {
    head = (
      <>
        <span className={clsx('font-medium', task.decision === 'reject' && 'text-danger')}>
          {DECISION_LABELS[task.decision ?? ''] ?? 'Выполнено'}
        </span>{' '}
        · {task.completedBy?.fullName ?? task.assignee.fullName}
        {task.completedAt && <span className="text-steel">, {formatMoment(task.completedAt)}</span>}
      </>
    );
  }
  return (
    <li className="text-[13.5px]">
      <p>{head}</p>
      {task.status !== 'skipped' && task.note && <p className="text-[12.5px] text-steel">{task.note}</p>}
      {task.comment && (
        <p className="mt-1 max-w-[70ch] whitespace-pre-wrap break-words border-l-2 border-line-strong pl-2.5 text-ink-soft">
          {task.comment}
        </p>
      )}
      {task.values.length > 0 && (
        <dl className="mt-1 grid grid-cols-[auto_1fr] gap-x-3 gap-y-0.5 text-[13px]">
          {task.values.map((v) => (
            <div key={v.label} className="contents">
              <dt className="text-steel">{v.label}</dt>
              <dd className="whitespace-pre-wrap break-words">
                {/^\d{4}-\d{2}-\d{2}$/.test(v.value) ? formatDay(v.value) : v.value}
              </dd>
            </div>
          ))}
        </dl>
      )}
      {task.files.length > 0 && (
        <div className="mt-1">
          <FileList files={task.files} />
        </div>
      )}
      {reassigning && <ReassignDialog task={task} onClose={() => setReassigning(false)} />}
    </li>
  );
}

function ReassignDialog({ task, onClose }: { task: ProcessTaskView; onClose: () => void }) {
  const refresh = useRefreshProcess();
  const [user, setUser] = useState<string[]>([]);
  const [error, setError] = useState<string | null>(null);
  const save = useMutation({
    mutationFn: () => api(`/bpm/tasks/${task.id}/reassign`, { method: 'POST', body: { userId: user[0] } }),
    onSuccess: async () => {
      await refresh();
      onClose();
    },
    onError: (err) => setError(err.message),
  });
  return (
    <Dialog title="Переназначить задание" onClose={onClose} width={480}>
      <div className="flex flex-col gap-4 px-5 py-4">
        <p className="text-steel">
          «{task.title}» сейчас у сотрудника {task.assignee.fullName}. Новый исполнитель получит уведомление.
        </p>
        <Field label="Кому передать">{(p) => <UserPicker id={p.id} value={user} onChange={setUser} />}</Field>
        {error && <p className="text-[13px] text-danger">{error}</p>}
      </div>
      <div className="flex justify-end gap-2 border-t border-line px-5 py-3">
        <Button variant="ghost" onClick={onClose}>
          Отмена
        </Button>
        <Button disabled={!user[0]} loading={save.isPending} onClick={() => save.mutate()}>
          Переназначить
        </Button>
      </div>
    </Dialog>
  );
}

/** Задание текущего пользователя: подсказка, поля, комментарий и кнопки решений. */
function MyTaskCard({ task }: { task: ProcessMyTask }) {
  const refresh = useRefreshProcess();
  const [files, setFiles] = useState<Record<string, FileItem[]>>({});
  const [errors, setErrors] = useState<Record<string, string>>({});
  const [formError, setFormError] = useState<string | null>(null);
  const [pending, setPending] = useState<ProcessAction['decision'] | null>(null);

  const decide = useMutation({
    mutationFn: (body: Record<string, unknown>) =>
      api(`/bpm/tasks/${task.id}/decide`, { method: 'POST', body }),
    onSuccess: refresh,
    onError: (err) => {
      if (err instanceof ApiError) {
        setErrors(err.fields);
        setFormError(Object.keys(err.fields).length ? err.message : err.message);
      }
    },
    onSettled: () => setPending(null),
  });

  function submit(action: ProcessAction, form: HTMLFormElement) {
    const fd = new FormData(form);
    const comment = String(fd.get('comment') ?? '').trim();
    setErrors({});
    setFormError(null);
    if (action.requireComment && !comment) {
      setErrors({ comment: 'Укажите причину' });
      return;
    }
    const data: Record<string, string | string[]> = {};
    if (action.decision === 'done') {
      for (const f of task.fields) {
        if (f.type === 'files') data[f.key] = (files[f.key] ?? []).map((x) => x.id);
        else data[f.key] = String(fd.get(`field-${f.key}`) ?? '');
      }
    }
    setPending(action.decision);
    decide.mutate({ decision: action.decision, comment: comment || undefined, data });
  }

  function onSubmit(e: FormEvent<HTMLFormElement>) {
    e.preventDefault();
    const primary = task.actions[0];
    if (primary) submit(primary, e.currentTarget);
  }

  const showFields = task.fields.length > 0;
  return (
    <section className="panel border-l-[3px] border-l-signal" aria-labelledby={`task-${task.id}`}>
      <form onSubmit={onSubmit} className="flex flex-col gap-4 px-5 py-4">
        <div>
          <p className="text-[12.5px] font-medium text-steel">
            {task.kind === 'approval' ? 'Ждёт вашего согласования' : 'Ваше задание'}
          </p>
          <h2 id={`task-${task.id}`} className="text-[16px] font-semibold">
            {task.title}
          </h2>
          {task.hint && <p className="mt-1 max-w-[70ch] text-steel">{task.hint}</p>}
        </div>
        {showFields &&
          task.fields.map((f) => (
            <Field key={f.key} label={f.required ? `${f.label} *` : f.label} error={errors[f.key]}>
              {(p) =>
                f.type === 'files' ? (
                  <FileUpload
                    id={p.id}
                    invalid={p['aria-invalid']}
                    value={files[f.key] ?? []}
                    onChange={(v) => setFiles((prev) => ({ ...prev, [f.key]: v }))}
                  />
                ) : f.type === 'textarea' ? (
                  <Textarea {...p} name={`field-${f.key}`} maxLength={10000} />
                ) : (
                  <Input
                    {...p}
                    name={`field-${f.key}`}
                    type={f.type === 'date' ? 'date' : 'text'}
                    maxLength={500}
                  />
                )
              }
            </Field>
          ))}
        <Field
          label="Комментарий"
          error={errors.comment}
          hint={
            task.actions.some((a) => a.requireComment)
              ? 'Обязателен, если отклоняете или возвращаете'
              : undefined
          }
        >
          {(p) => <Textarea {...p} name="comment" maxLength={5000} className="min-h-[64px]" />}
        </Field>
        {formError && !Object.keys(errors).length && (
          <p role="alert" className="text-[13px] text-danger">
            {formError}
          </p>
        )}
        <div className="flex flex-wrap gap-2">
          {task.actions.map((a) => (
            <Button
              key={a.decision}
              type={a.tone === 'primary' ? 'submit' : 'button'}
              variant={a.tone === 'primary' ? 'primary' : a.tone === 'danger' ? 'danger' : 'secondary'}
              loading={pending === a.decision}
              disabled={Boolean(pending)}
              onClick={
                a.tone === 'primary'
                  ? undefined
                  : (e) => {
                      const form = (e.currentTarget as HTMLButtonElement).form;
                      if (form) submit(a, form);
                    }
              }
            >
              {a.label}
            </Button>
          ))}
        </div>
      </form>
    </section>
  );
}
